import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";

import WidgetFormSkeleton from "../../../Components/Skeleton/widget-form-skeleton";

import { useModalContext } from "../../../Contexts/ModalContext";

import { useCardWidgetToken } from "../../../CustomHooks/Queries";
import { useSyncteraWidgetScripts } from "../../../CustomHooks/useSyncteraWidgets";

import { dispatchSuccessToast } from "../../../Utils/helpers";
import { APIQueryKeys, CARD_WIDGET_TOKEN_REFRESH_MS, isDevReactEnv, modalVariants } from "../../../Utils/constants";

import "./set-pin.scss";

const SYNCTERA_ENV = isDevReactEnv ? "sandbox" : "production";

export default function renderSetPin(modalContext, { title = "Set PIN", ...props } = {}) {
  const {
    updateModalComponent,
    pushModalStack,
    setModalVariant,
    setModalTitle,
    setModalClassName,
    setModalPromoComponent,
  } = modalContext;

  updateModalComponent(() => <SetPinModal {...props} />);
  pushModalStack(() => renderSetPin(modalContext, { title, ...props }));
  setModalPromoComponent(null);
  setModalTitle(title);
  setModalVariant(modalVariants.flexDialog);
  setModalClassName("set-pin-modal");
}

// `embedded` drops the modal chrome and the standalone note so a host (the checking
// wizard) can render the widget inline. onSuccess notifies the host that a PIN landed;
// it does not replace the dismissal, which still happens for the modal presentation.
export function SetPinModal({ cardId, styles, embedded = false, onSuccess }) {
  const ref = useRef(null);
  const { popModalStack } = useModalContext();
  const queryClient = useQueryClient();

  // Openers unmount as they hand off to this widget, taking their script tags with them.
  useSyncteraWidgetScripts();

  const [hasError, setHasError] = useState(false);

  const { data: tokenData, refetch: refetchToken } = useCardWidgetToken(
    { cardId, widgetType: "SET_PIN" },
    { enabled: !!cardId, staleTime: 0, refetchInterval: CARD_WIDGET_TOKEN_REFRESH_MS },
  );
  const token = tokenData?.widget_token;

  useEffect(() => {
    setHasError(false);
  }, [token]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !token) return;

    el.onError = () => {
      setHasError(true);
      refetchToken();
    };
    el.onSuccess = () => {
      dispatchSuccessToast("PIN updated successfully", { key: `set-pin-${cardId}` });
      // is_pin_set only reaches stake_debit once the CARD.UPDATED webhook lands, so
      // callers can't read it back right away — refetch and let onSuccess advance.
      queryClient.invalidateQueries(APIQueryKeys.stakeDebit);
      onSuccess?.();
      // Embedded hosts own their own layout, so there is no modal of ours to dismiss.
      if (!embedded) popModalStack(1);
    };
    // The Synctera <set-pin> widget renders its own failure message inline in the
    // pop-up (e.g. mismatched PINs), so we deliberately do not dispatch a toast here
    // to avoid showing the same error twice. We still refresh the (now consumed)
    // widget token so the user can retry.
    el.onFailure = () => {
      refetchToken();
    };

    return () => {
      el.onError = null;
      el.onSuccess = null;
      el.onFailure = null;
    };
  }, [token, cardId, refetchToken, popModalStack, queryClient, onSuccess, embedded]);

  const isLoading = !token || hasError;

  return (
    <div
      className={`set-pin-modal-content${embedded ? " set-pin-modal-content--embedded" : " modal-dialog-content"}`}
    >
      {!embedded && (
        <p className="set-pin-modal-note">
          Stores and ATMs sometimes require a PIN. Set one so those transactions go through.
        </p>
      )}
      {isLoading && <WidgetFormSkeleton isRefreshing={hasError} />}
      <div className="set-pin-modal-body" data-loading={isLoading}>
        {token && <set-pin ref={ref} token={token} env={SYNCTERA_ENV} theme="night-shift" styles={styles}></set-pin>}
      </div>
    </div>
  );
}
